import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useUser } from '@clerk/clerk-react';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { useSettings } from '../../contexts/SettingsContext';
import {
  POSITIONS,
  SCORING_TYPES,
  filterRows,
  readResults,
  sortRows,
  summarize,
  toRound,
  toRow,
} from './playerPool';

const COLUMNS = [
  { key: 'name', label: 'Player', numeric: false },
  { key: 'adpRound', label: 'ADP Round', numeric: true },
  { key: 'adpRaw', label: 'ADP', numeric: true },
  { key: 'adpRank', label: 'ADP Rank', numeric: true },
  { key: 'adpSource', label: 'Source', numeric: false },
  { key: 'ecrRank', label: 'ECR Rank', numeric: true },
  { key: 'ecrTier', label: 'Tier', numeric: true },
  { key: 'byeWeek', label: 'Bye', numeric: true },
];

const TEAM_COUNTS = [8, 10, 12, 14, 16];

const apiBase = () =>
  process.env.NODE_ENV === 'production' ? 'https://weez-nfl-data.vercel.app' : '';

const Players = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();
  const { positionColors } = useSettings();

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [scoringType, setScoringType] = useState('ppr');
  const [teamCount, setTeamCount] = useState(12);
  const [search, setSearch] = useState('');
  const [positions, setPositions] = useState([]);
  const [orderBy, setOrderBy] = useState('adpRank');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Deliberately uncached. The keeper page keeps its payload in localStorage for
  // a day, which is exactly how a stale board survives a backend fix -- this
  // page is meant to be the ground truth you check that against.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (user) {
          headers['X-Clerk-User-Id'] = user.id || 'unknown';
          headers['X-Clerk-Email'] = user.primaryEmailAddress?.emailAddress || '';
          headers['X-Clerk-Username'] = user.username || '';
        }
        const response = await fetch(`${apiBase()}/api/players/?live_draft=true`, { headers });
        if (!response.ok) {
          throw new Error(`Player pool request failed (${response.status})`);
        }
        const payload = await response.json();
        if (!cancelled) setPlayers(readResults(payload));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Every scoring board ships in the same payload, so switching between them is
  // a re-read rather than a re-fetch.
  const rows = useMemo(
    () => players.map((player) => toRow(player, scoringType)),
    [players, scoringType]
  );
  const summary = useMemo(() => summarize(rows), [rows]);

  const visible = useMemo(() => {
    const filtered = filterRows(rows, { search, positions });
    const withRound = filtered.map((row) => ({
      ...row,
      adpRound: toRound(row.adpRank, teamCount),
    }));
    return sortRows(withRound, orderBy, order);
  }, [rows, search, positions, teamCount, orderBy, order]);

  const pageRows = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrder(key === 'name' || key === 'adpSource' ? 'asc' : 'asc');
    }
    setPage(0);
  };

  const positionColor = (position) => {
    const fallback = {
      QB: 'rgba(239, 116, 161, 0.8)',
      RB: 'rgba(143, 242, 202, 0.8)',
      WR: 'rgba(86, 201, 248, 0.8)',
      TE: 'rgba(254, 174, 88, 0.8)',
      K: 'rgba(182, 185, 255, 0.8)',
      DST: 'rgba(191, 117, 93, 0.8)',
    };
    return positionColors?.[position] || fallback[position] || 'rgba(128, 128, 128, 0.8)';
  };

  const headerCellSx = {
    color: colors.grey[100],
    fontWeight: 600,
    backgroundColor: colors.primary[600],
    borderBottom: `1px solid ${colors.grey[700]}`,
    whiteSpace: 'nowrap',
  };

  const show = (value, suffix = '') =>
    value === null || value === undefined ? '—' : `${value}${suffix}`;

  return (
    <Box m="20px" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Header title="PLAYERS" subtitle="Browse the full player pool" />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          label="Search player or team"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Scoring"
          value={scoringType}
          onChange={(event) => setScoringType(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {SCORING_TYPES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Teams"
          value={teamCount}
          onChange={(event) => setTeamCount(Number(event.target.value))}
          sx={{ minWidth: 100 }}
          helperText="Sets ADP round"
        >
          {TEAM_COUNTS.map((count) => (
            <MenuItem key={count} value={count}>
              {count}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          value={positions}
          onChange={(event, next) => {
            setPositions(next);
            setPage(0);
          }}
        >
          {POSITIONS.map((position) => (
            <ToggleButton key={position} value={position} sx={{ px: 1.25 }}>
              {position}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography variant="caption" sx={{ color: colors.grey[300], mb: 1 }}>
        {`${summary.total} players · ${summary.withAdp} with ADP on this board · source: ${
          summary.sources.join(', ') || 'none'
        } · showing ${visible.length}`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography sx={{ color: colors.redAccent[400], py: 4 }}>{error}</Typography>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: colors.primary[500], flex: 1, minHeight: 0 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={headerCellSx}
                      align={column.numeric ? 'right' : 'left'}
                      sortDirection={orderBy === column.key ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? order : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow
                    key={row.id ?? `${row.name}-${row.position}`}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: alpha(colors.primary[400], 0.5) },
                      '&:hover': { backgroundColor: alpha(positionColor(row.position), 0.1) },
                    }}
                  >
                    <TableCell sx={{ color: colors.grey[100] }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {row.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        {row.position && (
                          <Chip
                            label={row.position}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              backgroundColor: alpha(positionColor(row.position), 0.8),
                              color: colors.primary[900],
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          {row.team || 'FA'}
                          {row.positionRank ? ` · ${row.position}${row.positionRank}` : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.grey[100] }}>
                      {row.adpRound === null ? '—' : `Round ${row.adpRound}`}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.grey[100] }}>
                      {row.adpRaw === null ? '—' : row.adpRaw.toFixed(1)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.grey[100] }}>
                      {show(row.adpRank)}
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[300] }}>
                      {row.adpSource || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.grey[100] }}>
                      {show(row.ecrRank)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.grey[100] }}>
                      {show(row.ecrTier)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.grey[100] }}>
                      {show(row.byeWeek)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(event, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            sx={{ color: colors.grey[100] }}
          />
        </>
      )}
    </Box>
  );
};

export default Players;
